import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, UtensilsCrossed, Users, Salad, ChefHat, GlassWater, Cake, Sandwich } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fullMenu, categoryBannerImages, DOORDASH_URL, UBEREATS_URL } from '../data/menu';
import { Button } from './ui/button';

const iconMap = { Flame, UtensilsCrossed, Users, Salad, ChefHat, GlassWater, Cake, Sandwich };

export function FullMenu() {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState(fullMenu[0].id);

  const activeData = fullMenu.find(c => c.id === activeCategory);

  return (
    <section id="full-menu" className="py-20 px-4 bg-white" data-testid="full-menu-section">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-cormorant text-4xl sm:text-5xl font-bold text-[#2C2C2C] mb-3" data-testid="full-menu-title">
            Our Full Menu
          </h2>
          <p className="font-manrope text-base text-[#666] max-w-xl mx-auto">
            Explore everything we have to offer — from charcoal-grilled favorites to authentic Iraqi specialties
          </p>
        </motion.div>

        {/* Category Tabs — Horizontal scroll on mobile */}
        <div className="mb-10 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          <div className="flex gap-2 min-w-max justify-center">
            {fullMenu.map((cat) => {
              const Icon = iconMap[cat.icon] || Flame;
              const isActive = activeCategory === cat.id;
              return (
                <motion.button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-manrope text-sm font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-[#6B1C23] text-white shadow-md'
                      : 'bg-[#f5f0ea] text-[#666] hover:bg-[#ebe4da] hover:text-[#333]'
                  }`}
                  data-testid={`menu-tab-${cat.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.category}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Active Category Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.25 }}
            className="bg-[#faf7f2] rounded-xl overflow-hidden border border-[#e8e0d4]"
            data-testid="menu-content"
          >
            {/* Category Banner Image */}
            {categoryBannerImages[activeCategory] && (
              <div className="relative h-44 sm:h-56 overflow-hidden">
                <motion.img
                  key={`img-${activeCategory}`}
                  src={categoryBannerImages[activeCategory]}
                  alt={activeData?.category}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.6 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/70 via-[#2C2C2C]/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6 sm:p-8">
                  <h3 className="font-cormorant text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">
                    {activeData?.category}
                  </h3>
                  {activeData?.note && (
                    <p className="font-manrope text-sm text-white/80 mt-1 drop-shadow">
                      {activeData.note}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Items Grid */}
            <div className="p-6 sm:p-10">
              <div className={`grid gap-3 ${
                activeData?.items.some(i => i.desc)
                  ? 'sm:grid-cols-1 lg:grid-cols-2'
                  : 'sm:grid-cols-2 lg:grid-cols-3'
              }`}>
                {activeData?.items.map((item, i) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    whileHover={{ x: 4, backgroundColor: 'rgba(255,255,255,0.8)' }}
                    className="flex items-start gap-3 p-3 rounded-lg transition-all cursor-default"
                    data-testid={`menu-item-${activeCategory}-${i}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#6B1C23] mt-2 shrink-0" />
                    <div>
                      <span className="font-manrope font-semibold text-[#2C2C2C] text-sm">
                        {item.name}
                      </span>
                      {item.desc && (
                        <p className="font-manrope text-xs text-[#888] mt-0.5">{item.desc}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Order CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-10"
        >
          <Button
            asChild
            className="bg-[#6B1C23] text-white hover:bg-[#8B2430] px-8 py-6 font-manrope"
            data-testid="full-menu-doordash-btn"
          >
            <a href={DOORDASH_URL} target="_blank" rel="noopener noreferrer">
              Order on DoorDash
            </a>
          </Button>
          <Button
            asChild
            className="bg-[#142328] text-white hover:bg-[#1f3640] px-8 py-6 font-manrope"
            data-testid="full-menu-ubereats-btn"
          >
            <a href={UBEREATS_URL} target="_blank" rel="noopener noreferrer">
              Order on Uber Eats
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
